import React from "react";
import { Link } from "react-router-dom";
import { Trophy, Star, MapPin, Phone, Globe, Clock, ArrowRight } from "lucide-react";
import { getStateName, getCourseUrl, getCleanDescription } from "../constants";

export const StarRating = ({ rating }) => (
  <div className="flex items-center gap-1.5">
    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
    <span className="text-sm font-semibold text-gray-900">{rating}</span>
  </div>
);

// Card used in "Top Rated Courses" sections - trophy icon, no image
export const TopRatedCard = ({ course }) => {
  const desc = getCleanDescription(course);
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200">
      <Trophy className="w-8 h-8 text-amber-500 mb-4" strokeWidth={2} />
      <h3 className="text-lg font-bold text-gray-900 leading-snug">
        <Link to={getCourseUrl(course)} className="hover:text-[#e5243b] transition-colors">
          {course.name}
        </Link>
      </h3>
      <div className="mt-2">
        <StarRating rating={course.rating} />
      </div>
      <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
        <MapPin className="w-3.5 h-3.5 text-gray-400" />
        {course.city}{course.state ? `, ${getStateName(course.state)}` : ""}
      </div>
      <p className="text-sm text-gray-600 mt-3 flex-1 break-words line-clamp-3">
        {desc}
      </p>
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
        <Link
          to={getCourseUrl(course)}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-[#e5243b] hover:text-[#b8182b] transition-colors"
        >
          View Details <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
};

// Card used in "Featured Courses" - with image
export const FeaturedCard = ({ course }) => {
  const desc = getCleanDescription(course);
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[box-shadow,transform] duration-300 flex flex-col group">
      <div className="h-48 overflow-hidden relative">
        <Link to={getCourseUrl(course)} className="block w-full h-full">
          <img
            src={course.image}
            alt={course.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </Link>
        <div className="absolute top-3 right-3 px-2.5 py-1 bg-[#e5243b] text-white text-xs font-bold rounded-lg shadow-md flex items-center gap-1">
          <Trophy className="w-3 h-3" /> Featured
        </div>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-lg font-bold text-gray-900">
          <Link to={getCourseUrl(course)} className="hover:text-[#e5243b] transition-colors">
            {course.name}
          </Link>
        </h3>
        <div className="flex items-center justify-between mt-2">
          <StarRating rating={course.rating} />
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <MapPin className="w-3.5 h-3.5 text-gray-400" />
            {course.city}, {getStateName(course.state)}
          </div>
        </div>
        {desc && (
          <p className="text-xs text-gray-600 mt-3 line-clamp-2 break-words">{desc}</p>
        )}
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">{course.priceRange || "Mini Golf"}</span>
          <Link
            to={getCourseUrl(course)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-[#e5243b] hover:bg-[#c9182d] rounded-lg shadow-xs transition-colors"
          >
            View Details <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// Card used on state/city listing pages - with image
export const ListingCard = ({ course }) => {
  const desc = getCleanDescription(course);
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-red-200 hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform,border-color] duration-200 flex flex-col group">
      <div className="h-44 overflow-hidden relative">
        <Link to={getCourseUrl(course)} className="block w-full h-full">
          <img src={course.image} alt={course.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        </Link>
        {course.featured && (
          <div className="absolute top-2.5 left-2.5 px-2 py-0.5 bg-[#e5243b] text-white text-[11px] font-bold rounded-md shadow">
            Featured
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-base font-bold text-gray-900 leading-snug">
          <Link to={getCourseUrl(course)} className="hover:text-[#e5243b] transition-colors">
            {course.name}
          </Link>
        </h3>
        <div className="flex items-center gap-3 mt-2">
          <StarRating rating={course.rating} />
          <span className="text-xs text-gray-400">({course.reviewCount} reviews)</span>
        </div>
        <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
          <MapPin className="w-3.5 h-3.5 text-gray-400" />
          {course.city}, {getStateName(course.state)}
        </div>
        {desc && (
          <p className="text-xs text-gray-600 mt-2.5 line-clamp-2 break-words">{desc}</p>
        )}
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">{course.priceRange || "Mini Golf"}</span>
          <Link
            to={getCourseUrl(course)}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold text-white bg-[#e5243b] hover:bg-[#c9182d] rounded-lg shadow-xs transition-colors"
          >
            View Details <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// Card matching the City Destination page screenshot
export const CityListingCard = ({ course }) => {
  const mondayHours = course.hours?.Monday || "10:00 AM - 9:00 PM";
  const desc = getCleanDescription(course);

  return (
    <div className="bg-[#181a20] border border-[#272a34] rounded-2xl overflow-hidden hover:border-gray-600 transition-all duration-300 shadow-xl flex flex-col group">
      <div className="h-52 overflow-hidden relative">
        <Link to={getCourseUrl(course)} className="block w-full h-full">
          <img
            src={course.image}
            alt={course.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </Link>
        {course.featured && (
          <div className="absolute top-3 left-3 px-2.5 py-1 bg-[#e5243b] text-white text-xs font-bold rounded-lg shadow-md flex items-center gap-1">
            <Trophy className="w-3 h-3" /> Featured
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        {/* Title & Rating */}
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-bold text-white leading-snug">
            <Link to={getCourseUrl(course)} className="hover:text-red-400 transition-colors">
              {course.name}
            </Link>
          </h3>
          <div className="flex items-center gap-1 text-amber-400 font-bold text-sm flex-shrink-0">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span>{course.rating}</span>
          </div>
        </div>

        {/* Location / Phone / Website / Hours */}
        <div className="mt-3.5 space-y-2 text-xs text-gray-300/90 font-normal">
          {course.address && (
            <div className="flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
              <span className="truncate">{course.address}</span>
            </div>
          )}
          {course.phone && (
            <div className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <a href={`tel:${course.phone}`} className="hover:text-white transition-colors">
                {course.phone}
              </a>
            </div>
          )}
          {course.website && (
            <div className="flex items-center gap-2">
              <Globe className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <a
                href={course.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white underline underline-offset-2 transition-colors"
              >
                Visit Website
              </a>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span>
              Monday: {mondayHours}
            </span>
          </div>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-400 mt-4 leading-relaxed line-clamp-3 break-words">
          {desc}
        </p>

        {/* Actions - View Details */}
        <div className="mt-5 pt-4 border-t border-[#272a34] flex items-center justify-between gap-3">
          <Link
            to={getCourseUrl(course)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#e5243b] hover:bg-[#c9182d] text-white text-xs font-bold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
          >
            View Details <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};
